import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Brand from './Brand';

const { FiArrowLeft, FiCheck, FiFileText, FiSearch, FiTrendingDown, FiRefreshCw } = FiIcons;

const benefits = [
  ['اكتشف أسباب الخسارة', 'اعرف لماذا لا تتحول بعض الاستفسارات إلى حجوزات.', FiSearch],
  ['احسب التسرب المالي', 'حوّل العملاء المفقودين إلى تقدير مالي واضح.', FiTrendingDown],
  ['اكتشف الفرص القابلة للاستعادة', 'حدد العملاء الذين يستحقون متابعة جديدة.', FiRefreshCw],
  ['احصل على توصيات واضحة', 'اعرف ما الذي ينبغي تحسينه بدلًا من الأرقام فقط.', FiCheck]
];

export default function Landing({ language, setLanguage, navigate }) {
  const english = language === 'en';

  if (english) {
    return (
      <div className="landing" dir="ltr">
        <Header language={language} setLanguage={setLanguage} navigate={navigate} />
        <main className="english-intro">
          <span className="eyebrow">Revenue Intelligence for Clinics</span>
          <h1>Discover where your clinic is losing patients and revenue.</h1>
          <p>Cencey turns lead and conversion data into clear leakage estimates, reasons, and next actions.</p>
          <div className="hero-actions">
            <button className="btn primary" onClick={() => navigate('/signup')}>Analyze your clinic data</button>
            <button className="btn secondary" onClick={() => navigate('/dashboard')}>View demo</button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="landing">
      <Header language={language} setLanguage={setLanguage} navigate={navigate} />
      <main>
        <section className="hero">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="hero-copy">
            <span className="eyebrow">ذكاء الإيرادات للعيادات</span>
            <h1>اكتشف أين تخسر عيادتك المرضى والإيرادات</h1>
            <p>يحلل Cencey بيانات العملاء المحتملين ليكشف أسباب ضياع فرص البيع، وحجم الإيرادات المعرضة للخسارة، والفرص التي لا يزال بإمكانك استعادتها.</p>
            <div className="hero-actions">
              <button className="btn primary" onClick={() => navigate('/signup')}>حلّل بيانات عيادتك مجانًا <SafeIcon icon={FiArrowLeft} /></button>
              <button className="btn secondary" onClick={() => navigate('/dashboard')}>شاهد مثالًا</button>
            </div>
            <small className="reassurance"><SafeIcon icon={FiCheck} /> لا تحتاج إلى تغيير نظامك الحالي. ابدأ بملف CSV أو Excel.</small>
          </motion.div>
          <DashboardPreview />
        </section>

        <section className="problem section">
          <div>
            <span className="eyebrow">المشكلة</span>
            <h2>العملاء يصلون إلى عيادتك... لكن أين يذهبون بعد ذلك؟</h2>
            <p>قد تدفع عيادتك للإعلانات وتستقبل استفسارات كثيرة، لكن بعض العملاء المحتملين يختفون قبل الحجز أو قبول العلاج.</p>
            <blockquote>المشكلة ليست دائمًا في الإعلانات. أحيانًا يكون التسرب بعد وصول العميل.</blockquote>
          </div>
          <div className="reason-cloud">
            {['لم تتم المتابعة', 'تأخر الرد', 'اعتراض على السعر', 'عدم توفر موعد مناسب', 'اختيار منافس', 'عدم المتابعة بعد الاستشارة'].map((item) => <span key={item}>{item}</span>)}
          </div>
        </section>

        <section className="section centered" id="how">
          <span className="eyebrow">كيف يعمل؟</span>
          <h2>من البيانات إلى القرار في خطوات بسيطة</h2>
          <div className="steps">
            {[
              ['01', 'ارفع بيانات العملاء', 'CSV أو Excel.', FiFileText],
              ['02', 'Cencey يحلل البيانات', 'يكتشف أنماط الخسارة والتسرب.', FiSearch],
              ['03', 'اعرف أين تخسر الإيرادات', 'شاهد الأسباب والخدمات الأكثر تأثرًا.', FiTrendingDown],
              ['04', 'استعد الفرص الممكنة', 'حدد العملاء الذين يستحقون متابعة جديدة.', FiRefreshCw]
            ].map(([number, title, text, icon]) => (
              <article className="step-card" key={number}>
                <span className="step-number">{number}</span>
                <SafeIcon icon={icon} />
                <h3>{title}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="section centered" id="features">
          <span className="eyebrow">وضوح يقود إلى القرار</span>
          <h2>كل ما تحتاجه لفهم تسرب الإيرادات</h2>
          <div className="benefit-grid">
            {benefits.map(([title, text, icon]) => (
              <article className="benefit-card" key={title}>
                <SafeIcon icon={icon} />
                <h3>{title}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="section insight-showcase">
          <div>
            <span className="eyebrow light">تحليل واضح، لا تعقيد إضافي</span>
            <h2>لا يعرض Cencey الأرقام فقط</h2>
            <p>يشرح لك ما يحدث، ويحوّله إلى إجراء واضح يمكن لفريقك تنفيذه.</p>
            <button className="btn light-btn" onClick={() => navigate('/dashboard')}>شاهد كيف يعمل</button>
          </div>
          <article className="showcase-card">
            <span className="analysis-label">تحليل Cencey</span>
            <h3>أكبر مشكلة لديك ليست السعر.</h3>
            <p>31% من الفرص المفقودة لم تحصل على متابعة ثانية.</p>
            <hr />
            <small>الإيرادات المقدّرة المعرضة للخسارة بسبب ذلك</small>
            <strong>21,200 ر.س</strong>
            <div className="recommendation"><b>التوصية:</b> إجراء متابعة ثانية خلال 48 ساعة للعملاء المؤهلين.</div>
          </article>
        </section>

        <section className="section centered audience">
          <h2>مصمم لعيادات الأسنان التي تريد زيادة التحويل، لا زيادة التعقيد</h2>
          <div>{['عيادات الأسنان', 'مراكز زراعة الأسنان', 'مراكز التقويم', 'عيادات تجميل الأسنان', 'مجموعات العيادات'].map((item) => <span key={item}><SafeIcon icon={FiCheck} /> {item}</span>)}</div>
        </section>

        <section className="final-cta">
          <h2>قد لا تحتاج إلى المزيد من العملاء... بل إلى خسارة عدد أقل منهم</h2>
          <p>ارفع بياناتك واكتشف أين تتسرب الإيرادات من رحلة العميل.</p>
          <button className="btn light-btn" onClick={() => navigate('/signup')}>ابدأ التحليل المجاني</button>
        </section>
      </main>
      <Footer setLanguage={setLanguage} />
    </div>
  );
}

function Header({ language, setLanguage, navigate }) {
  return (
    <header className="public-header">
      <Brand />
      <nav>
        <a href="#top">الرئيسية</a>
        <a href="#how">كيف يعمل؟</a>
        <a href="#features">المزايا</a>
        <a href="#faq">الأسئلة الشائعة</a>
      </nav>
      <div className="header-actions">
        <button className="language-btn" onClick={() => setLanguage(language === 'ar' ? 'en' : 'ar')}>{language === 'ar' ? 'العربية | English' : 'English | العربية'}</button>
        <button className="btn primary compact" onClick={() => navigate('/signup')}>حلّل بيانات عيادتك مجانًا</button>
      </div>
    </header>
  );
}

function DashboardPreview() {
  return (
    <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.15 }} className="dashboard-preview">
      <div className="preview-head"><Brand compact /><span>أغسطس 2026</span></div>
      <small>الإيرادات المقدّرة المفقودة</small>
      <strong className="preview-total">68,450 <i>ر.س</i></strong>
      <div className="preview-grid">
        <div><small>أكبر سبب للخسارة</small><b>عدم المتابعة — 31%</b></div>
        <div><small>فرص قابلة للاستعادة</small><b>17</b></div>
        <div><small>إيرادات قابلة للاستعادة</small><b>21,200 ر.س</b></div>
      </div>
      <div className="mini-bars">
        {[31, 24, 18, 11].map((value, index) => <span key={value} style={{ width: `${value * 2.5}%`, opacity: 1 - index * 0.16 }} />)}
      </div>
    </motion.div>
  );
}

function Footer({ setLanguage }) {
  return (
    <footer>
      <Brand />
      <div><a>الخصوصية</a><a>الشروط</a><a>تواصل معنا</a><button onClick={() => setLanguage('en')}>English</button></div>
      <p>© 2026 Cencey</p>
    </footer>
  );
}